import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, ArrowRight, CheckCircle2, Loader2, Package, RotateCcw, Truck } from 'lucide-react';
import Captcha, { CaptchaHandle } from '../components/Captcha';
import { useSeo } from '../utils/seo';

/**
 * The customer returns page. No login: the store links here with its company
 * id, the customer proves the order with its number, the email on it and the delivery ZIP,
 * ticks the delivered shipments the goods come back from and
 * the items in them, and the request lands in the Returns grid as Pending for
 * staff to authorize. Everything after that is done from the portal.
 */

interface PortalInfo {
  companyName: string;
  companyLogo: string | null;
  portalEnabled: boolean;
  returnWindowDays: number;
  reasons: Array<{ code: string; label: string }>;
  conditions: Array<{ code: string; label: string }>;
  resolutions: Array<{ code: string; label: string }>;
}

interface Tracking {
  shipmentId: string;
  transactionId: string;
  trackingNumbers: string[];
  carrier: string | null;
  status: string;
  delivered: boolean;
  deliveredAt: string | null;
  items: Array<{ orderItemId: string | null; sku: string; qty: number }>;
}

interface Line {
  orderItemId: string;
  sku: string;
  name: string | null;
  qtyOrdered: number;
  qtyDelivered: number | null;
  deliveredTrackingNumbers: string[];
  qtyReturnable: number;
}

interface Lookup {
  token: string;
  order: {
    orderNumber: string | null;
    status: string | null;
    placedAt: string;
    customerName: string | null;
    customerEmailMasked: string | null;
    shipTo: { name: string | null; city: string | null; state: string | null; zip: string | null };
  };
  lines: Line[];
  trackings: Tracking[];
  trackingSummary: { total: number; delivered: number; hasTrackingData: boolean };
  policy: { returnWindowDays: number; ageDays: number; withinWindow: boolean; statusEligible: boolean };
  existingReturns: Array<{ rmaNumber: string; status: string; createdAt: string }>;
}

interface LineState {
  selected: boolean;
  qty: string;
  reasonCode: string;
  reasonText: string;
  conditionCode: string;
  resolutionCode: string;
  note: string;
}

interface Submitted {
  rmaNumber: string;
  status: string;
  emailedTo: string | null;
  items: Array<{ sku: string; name: string | null; qtyRequested: number }>;
}

const getApiBase = () => {
  const envApi = (import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_BASE || '').trim();
  if (envApi) return `${envApi.replace(/\/v1(\/admin|\/api)?.*$/, '')}/v1`;
  return 'http://api.magento.local/v1';
};

const PUBLIC = `${getApiBase()}/api/public/returns`;

const input = 'w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#0047AB] focus:outline-none focus:ring-2 focus:ring-[#0047AB]/20';
const select = 'w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 focus:border-[#0047AB] focus:outline-none';
const labelCls = 'block text-xs font-semibold text-slate-700 mb-1';
const primary = 'inline-flex items-center justify-center gap-2 rounded-lg bg-[#0047AB] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#003a8c] disabled:opacity-60';
const secondary = 'inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50';

const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const statusWord = (s: string) => String(s || '').replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());

/** Units of `line` inside the given shipments (matched by the store line id, else by SKU). */
const unitsIn = (line: Line, trackings: Tracking[]) =>
  trackings.reduce((acc, t) => acc + t.items.filter((i) => (i.orderItemId != null ? i.orderItemId === line.orderItemId : i.sku === line.sku)).reduce((a, i) => a + i.qty, 0), 0);

const Alert = ({ tone, children }: { tone: 'error' | 'warn' | 'info'; children: React.ReactNode }) => {
  const cls = tone === 'error' ? 'bg-rose-50 border-rose-200 text-rose-800' : tone === 'warn' ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-blue-50 border-blue-200 text-blue-900';
  return (
    <div className={`flex items-start gap-2 rounded-lg border px-3 py-2.5 text-sm ${cls}`}>
      <AlertCircle size={16} className="mt-0.5 shrink-0" />
      <div>{children}</div>
    </div>
  );
};

export default function RequestReturn() {
  useSeo({ title: 'Request a Return', description: 'Start a return for your order.', sensitive: true });
  const [params] = useSearchParams();
  const company = (params.get('company') || params.get('c') || '').trim();

  const [info, setInfo] = useState<PortalInfo | null>(null);
  const [infoError, setInfoError] = useState('');
  const [step, setStep] = useState<1 | 2 | 3>(1);

  // Step 1
  const [orderNumber, setOrderNumber] = useState(params.get('order') || '');
  const [lookupEmail, setLookupEmail] = useState('');
  const [zip, setZip] = useState('');
  const [captcha1, setCaptcha1] = useState('');
  const [captcha1Key, setCaptcha1Key] = useState(0);
  const captcha1Ref = useRef<CaptchaHandle>(null);
  const [looking, setLooking] = useState(false);
  const [lookupError, setLookupError] = useState('');

  // Step 2
  const [data, setData] = useState<Lookup | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [lines, setLines] = useState<Record<string, LineState>>({});
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [comment, setComment] = useState('');
  const [captcha2, setCaptcha2] = useState('');
  const [captcha2Key, setCaptcha2Key] = useState(0);
  const captcha2Ref = useRef<CaptchaHandle>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Step 3
  const [done, setDone] = useState<Submitted | null>(null);

  useEffect(() => {
    if (!company) {
      setInfoError('This returns link is missing the store it belongs to. Please use the link on the store’s website.');
      return;
    }
    fetch(`${PUBLIC}/info?company=${encodeURIComponent(company)}`)
      .then(async (res) => {
        const json = await res.json().catch(() => null);
        if (!res.ok) throw new Error(json?.message || 'This returns page is not available.');
        return json as PortalInfo;
      })
      .then(setInfo)
      .catch((err) => setInfoError(err.message || 'This returns page is not available.'));
  }, [company]);

  const resetCaptcha1 = () => {
    captcha1Ref.current?.reset();
    setCaptcha1('');
    setCaptcha1Key((k) => k + 1);
  };
  const resetCaptcha2 = () => {
    captcha2Ref.current?.reset();
    setCaptcha2('');
    setCaptcha2Key((k) => k + 1);
  };

  const lookup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLookupError('');
    if (!orderNumber.trim() || !lookupEmail.trim() || !zip.trim()) {
      setLookupError('Please fill in your order number, email address and the delivery ZIP code.');
      return;
    }
    if (!captcha1) {
      setLookupError('Please complete the security check.');
      return;
    }
    setLooking(true);
    try {
      const res = await fetch(`${PUBLIC}/lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ company, orderNumber: orderNumber.trim(), email: lookupEmail.trim(), zip: zip.trim(), captchaToken: captcha1 }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) throw new Error(json?.message || 'We could not find that order.');
      const result = json as Lookup;
      setData(result);
      // One delivered shipment: tick it so the items show straight away.
      const delivered = result.trackings.filter((t) => t.delivered);
      const start = new Set<string>(delivered.length === 1 ? [delivered[0].shipmentId] : []);
      setPicked(start);
      const chosen = result.trackings.filter((t) => start.has(t.shipmentId));
      const init: Record<string, LineState> = {};
      for (const l of result.lines) {
        const cap = Math.min(l.qtyReturnable, unitsIn(l, chosen));
        init[l.orderItemId] = { selected: false, qty: String(Math.max(1, cap)), reasonCode: '', reasonText: '', conditionCode: '', resolutionCode: '', note: '' };
      }
      setLines(init);
      setStep(2);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      setLookupError(err.message || 'We could not find that order. Please try again.');
    } finally {
      resetCaptcha1();
      setLooking(false);
    }
  };

  const hasTrackings = Boolean(data?.trackingSummary.hasTrackingData);
  const pickedTrackings = useMemo(() => (data ? data.trackings.filter((t) => picked.has(t.shipmentId)) : []), [data, picked]);

  /** Lines on offer: what the ticked, delivered shipments carried, capped by what may still come back. */
  const visible = useMemo(() => {
    if (!data) return [] as Array<{ line: Line; cap: number; trackings: string[] }>;
    return data.lines
      .map((line) => {
        const inPicked = unitsIn(line, pickedTrackings);
        const carrying = pickedTrackings.filter((t) => unitsIn(line, [t]) > 0);
        return { line, cap: Math.min(line.qtyReturnable, inPicked), trackings: carrying.flatMap((t) => t.trackingNumbers), inPicked };
      })
      .filter((v) => v.inPicked > 0);
  }, [data, pickedTrackings]);

  const toggleTracking = (t: Tracking) => {
    if (!t.delivered || !data) return;
    const next = new Set(picked);
    if (next.has(t.shipmentId)) next.delete(t.shipmentId);
    else next.add(t.shipmentId);
    setPicked(next);
    const nowPicked = data.trackings.filter((x) => next.has(x.shipmentId));
    setLines((prev) => {
      const out = { ...prev };
      for (const line of data.lines) {
        const cap = Math.min(line.qtyReturnable, unitsIn(line, nowPicked));
        out[line.orderItemId] = { ...out[line.orderItemId], qty: String(Math.max(1, cap)), ...(cap <= 0 ? { selected: false } : {}) };
      }
      return out;
    });
  };

  const setLine = (key: string, patch: Partial<LineState>) => setLines((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  const selected = useMemo(() => visible.filter((v) => v.cap > 0 && lines[v.line.orderItemId]?.selected), [visible, lines]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!data) return;
    setSubmitError('');
    if (selected.length === 0) {
      setSubmitError('Tick at least one item to return.');
      return;
    }
    const items: any[] = [];
    for (const { line, cap, trackings } of selected) {
      const s = lines[line.orderItemId];
      const qty = Number(s.qty);
      if (!Number.isInteger(qty) || qty < 1 || qty > cap) {
        setSubmitError(`${line.name || line.sku}: quantity must be between 1 and ${cap}.`);
        return;
      }
      if (!s.reasonCode) {
        setSubmitError(`${line.name || line.sku}: please pick a reason.`);
        return;
      }
      items.push({
        orderItemId: line.orderItemId,
        sku: line.sku,
        qtyRequested: qty,
        outboundTrackingNumbers: trackings,
        reasonCode: s.reasonCode,
        reasonText: s.reasonText || null,
        conditionCode: s.conditionCode || null,
        resolutionCode: s.resolutionCode || null,
        customerNote: s.note || null,
      });
    }
    if (!captcha2) {
      setSubmitError('Please complete the security check.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${PUBLIC}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: data.token, items, customerEmail: email.trim() || undefined, customerPhone: phone.trim() || undefined, customerComment: comment.trim() || undefined, captchaToken: captcha2 }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) throw new Error(json?.message || 'We could not submit your return.');
      setDone(json as Submitted);
      setStep(3);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      setSubmitError(err.message || 'We could not submit your return. Please try again.');
      resetCaptcha2();
    } finally {
      setSubmitting(false);
    }
  };

  const startOver = () => {
    setData(null);
    setDone(null);
    setPicked(new Set());
    setLines({});
    setEmail('');
    setPhone('');
    setComment('');
    setSubmitError('');
    setStep(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const shell = (children: React.ReactNode) => (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex flex-col">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          {info?.companyLogo ? (
            <img src={info.companyLogo} alt={info.companyName} className="h-9 max-w-[160px] object-contain" />
          ) : (
            <div className="h-9 w-9 rounded-lg bg-[#0047AB] text-white flex items-center justify-center">
              <RotateCcw size={18} />
            </div>
          )}
          <div>
            <div className="text-sm font-bold">{info?.companyName || 'Returns'}</div>
            <div className="text-xs text-slate-500">Request a return</div>
          </div>
        </div>
      </header>
      <main className="flex-1 max-w-3xl w-full mx-auto px-4 py-8">{children}</main>
      <footer className="py-6 text-center text-xs text-slate-500">Every request is reviewed by {info?.companyName || 'the store'} before anything is sent back.</footer>
    </div>
  );

  if (infoError) {
    return shell(<Alert tone="error">{infoError}</Alert>);
  }
  if (!info) {
    return shell(
      <div className="flex items-center gap-2 text-sm text-slate-600">
        <Loader2 size={16} className="animate-spin" /> Loading…
      </div>,
    );
  }
  if (!info.portalEnabled) {
    return shell(<Alert tone="warn">{info.companyName} does not accept return requests online. Please contact customer service.</Alert>);
  }

  const steps = ['Find your order', 'Choose items', 'Done'];
  const stepper = (
    <ol className="flex items-center gap-2 mb-6 text-xs font-semibold">
      {steps.map((s, i) => {
        const n = (i + 1) as 1 | 2 | 3;
        const on = step === n;
        const past = step > n;
        return (
          <li key={s} className="flex items-center gap-2">
            <span className={`h-6 w-6 rounded-full flex items-center justify-center ${on ? 'bg-[#0047AB] text-white' : past ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-600'}`}>{past ? '✓' : n}</span>
            <span className={on ? 'text-slate-900' : 'text-slate-500'}>{s}</span>
            {i < steps.length - 1 && <span className="w-6 h-px bg-slate-300 mx-1" />}
          </li>
        );
      })}
    </ol>
  );

  /* ── Step 1: find the order ─────────────────────────────────────────── */
  if (step === 1) {
    return shell(
      <>
        {stepper}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
          <h1 className="text-lg font-bold">Find your order</h1>
          <p className="text-sm text-slate-600 mt-1 mb-5">Enter your order number, the email address you ordered with and the ZIP code the order was delivered to, as shown on your order confirmation.</p>
          <form onSubmit={lookup} className="space-y-4">
            <div>
              <label className={labelCls}>Order number</label>
              <input className={input} value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} placeholder="e.g. 100012345" autoComplete="off" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="sm:col-span-2">
                <label className={labelCls}>Email address</label>
                <input type="email" className={input} value={lookupEmail} onChange={(e) => setLookupEmail(e.target.value)} placeholder="The email you ordered with" autoComplete="email" />
              </div>
              <div>
                <label className={labelCls}>ZIP / postal code</label>
                <input className={input} value={zip} onChange={(e) => setZip(e.target.value)} placeholder="e.g. 90210" autoComplete="postal-code" />
              </div>
            </div>
            <Captcha key={captcha1Key} ref={captcha1Ref} onVerify={setCaptcha1} />
            {lookupError && <Alert tone="error">{lookupError}</Alert>}
            <div className="flex justify-end">
              <button type="submit" className={primary} disabled={looking}>
                {looking ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                {looking ? 'Looking up…' : 'Find my order'}
              </button>
            </div>
          </form>
        </div>
        {info.returnWindowDays > 0 && <p className="text-xs text-slate-500 mt-4">Returns can be requested up to {info.returnWindowDays} days after the order was placed.</p>}
      </>,
    );
  }

  /* ── Step 3: done ────────────────────────────────────────────────────── */
  if (step === 3 && done) {
    return shell(
      <>
        {stepper}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 text-center">
          <div className="mx-auto h-12 w-12 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mb-3">
            <CheckCircle2 size={26} />
          </div>
          <h1 className="text-lg font-bold">Return request received</h1>
          <p className="text-sm text-slate-600 mt-1">
            Your reference is <span className="font-mono font-bold text-slate-900">{done.rmaNumber}</span>. Please keep it for any questions.
          </p>
          <div className="mt-5 text-left bg-slate-50 rounded-xl border border-slate-200 p-4">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">What you asked to return</div>
            <ul className="text-sm space-y-1">
              {done.items.map((i, idx) => (
                <li key={idx} className="flex justify-between gap-3">
                  <span>{i.name || i.sku}</span>
                  <span className="font-semibold">×{i.qtyRequested}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-5 text-left bg-blue-50 rounded-xl border border-blue-200 p-4 text-sm text-blue-900">
            <div className="font-bold mb-1">What happens next</div>
            <p>
              {info.companyName} will review your request. Please do not send anything back yet: once it is approved you will receive the return address and instructions
              {done.emailedTo ? ` at ${done.emailedTo}` : ''}.
            </p>
          </div>
          <button type="button" className={`${secondary} mt-6`} onClick={startOver}>
            <RotateCcw size={14} /> Start another return
          </button>
        </div>
      </>,
    );
  }

  /* ── Step 2: choose shipments and items ─────────────────────────────── */
  if (!data) return shell(null);
  const o = data.order;
  const blocked = !data.policy.statusEligible || !data.policy.withinWindow || !hasTrackings;

  return shell(
    <>
      {stepper}
      <div className="space-y-5">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Order</div>
            <div className="text-base font-bold">#{o.orderNumber}</div>
            <div className="text-xs text-slate-600">
              Placed {fmtDate(o.placedAt)}
              {o.shipTo.city ? ` · Delivered to ${[o.shipTo.city, o.shipTo.state, o.shipTo.zip].filter(Boolean).join(', ')}` : ''}
            </div>
          </div>
          <button type="button" className={secondary} onClick={startOver}>
            <ArrowLeft size={14} /> Different order
          </button>
        </div>

        {!data.policy.statusEligible && <Alert tone="warn">This order is not eligible for a return. Please contact customer service.</Alert>}
        {data.policy.statusEligible && !data.policy.withinWindow && (
          <Alert tone="warn">The {data.policy.returnWindowDays}-day return window for this order has passed. Please contact customer service.</Alert>
        )}
        {data.existingReturns.length > 0 && (
          <Alert tone="info">
            You already have {data.existingReturns.length === 1 ? 'a return' : `${data.existingReturns.length} returns`} on this order:{' '}
            {data.existingReturns.map((r) => `${r.rmaNumber} (${statusWord(r.status)})`).join(', ')}.
          </Alert>
        )}

        <form onSubmit={submit} className="space-y-5">
          {/* Shipments: only what was delivered on a tracking can come back. */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <div className="flex items-center gap-2 mb-1">
              <Truck size={16} className="text-[#0047AB]" />
              <h2 className="text-sm font-bold">Shipments</h2>
            </div>
            <p className="text-xs text-slate-600 mb-3">Tick the delivered shipment(s) the items are coming back from.</p>
            {!hasTrackings ? (
              <Alert tone="warn">Nothing from this order has shipped yet, so there is nothing to return. Please check back once your items have been delivered.</Alert>
            ) : (
              <div className="space-y-2">
                {data.trackings.map((t) => {
                  const on = picked.has(t.shipmentId);
                  return (
                    <label
                      key={t.shipmentId}
                      className={`flex items-start gap-3 rounded-xl border p-3 ${t.delivered ? 'cursor-pointer hover:bg-slate-50' : 'opacity-60'} ${on ? 'border-[#0047AB] bg-blue-50/50' : 'border-slate-200'}`}
                    >
                      <input type="checkbox" className="mt-1" checked={on} disabled={!t.delivered} onChange={() => toggleTracking(t)} />
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                          <span className="font-mono text-sm font-semibold">{t.trackingNumbers.join(', ') || t.transactionId}</span>
                          {t.carrier && <span className="text-xs uppercase text-slate-600">{t.carrier}</span>}
                          <span className={`text-xs font-bold ${t.delivered ? 'text-emerald-700' : 'text-slate-600'}`}>
                            {t.delivered ? `Delivered${t.deliveredAt ? ` ${fmtDate(t.deliveredAt)}` : ''}` : `${statusWord(t.status)} · not returnable until delivered`}
                          </span>
                        </div>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {t.items.map((i, idx) => {
                            const line = data.lines.find((l) => (i.orderItemId != null ? l.orderItemId === i.orderItemId : l.sku === i.sku));
                            return (
                              <span key={`${i.sku}-${idx}`} className="inline-flex items-center rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-800">
                                {line?.name || i.sku} ×{i.qty}
                              </span>
                            );
                          })}
                          {t.items.length === 0 && <span className="text-xs text-slate-500">Contents not recorded</span>}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* Items in the ticked shipments. */}
          {hasTrackings && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
              <div className="flex items-center gap-2 mb-1">
                <Package size={16} className="text-[#0047AB]" />
                <h2 className="text-sm font-bold">Items to return</h2>
              </div>
              {pickedTrackings.length === 0 ? (
                <p className="text-sm text-slate-600">Tick a delivered shipment above to see its items.</p>
              ) : visible.length === 0 ? (
                <p className="text-sm text-slate-600">The ticked shipment(s) carry nothing that can still be returned.</p>
              ) : (
                <div className="space-y-3 mt-2">
                  {visible.map(({ line, cap }) => {
                    const s = lines[line.orderItemId];
                    const exhausted = cap <= 0;
                    return (
                      <div key={line.orderItemId} className={`rounded-xl border p-3 ${s?.selected ? 'border-[#0047AB] bg-blue-50/40' : 'border-slate-200'} ${exhausted ? 'opacity-60' : ''}`}>
                        <label className={`flex items-start gap-3 ${exhausted ? '' : 'cursor-pointer'}`}>
                          <input type="checkbox" className="mt-1" checked={Boolean(s?.selected)} disabled={exhausted} onChange={(e) => setLine(line.orderItemId, { selected: e.target.checked })} />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold">{line.name || line.sku}</div>
                            <div className="text-xs text-slate-600">
                              SKU {line.sku} · {exhausted ? 'already requested or returned' : `up to ${cap} of ${line.qtyOrdered} can be returned`}
                            </div>
                          </div>
                        </label>
                        {s?.selected && (
                          <div className="mt-3 grid grid-cols-1 sm:grid-cols-4 gap-3 pl-7">
                            <div>
                              <label className={labelCls}>Quantity</label>
                              <input type="number" min={1} max={cap} className={select} value={s.qty} onChange={(e) => setLine(line.orderItemId, { qty: e.target.value })} />
                            </div>
                            <div className="sm:col-span-3">
                              <label className={labelCls}>Reason *</label>
                              <select className={select} value={s.reasonCode} onChange={(e) => setLine(line.orderItemId, { reasonCode: e.target.value })}>
                                <option value="">Choose a reason</option>
                                {info.reasons.map((r) => (
                                  <option key={r.code} value={r.code}>
                                    {r.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            {info.conditions.length > 0 && (
                              <div className="sm:col-span-2">
                                <label className={labelCls}>Condition of the item</label>
                                <select className={select} value={s.conditionCode} onChange={(e) => setLine(line.orderItemId, { conditionCode: e.target.value })}>
                                  <option value="">Not sure</option>
                                  {info.conditions.map((c) => (
                                    <option key={c.code} value={c.code}>
                                      {c.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                            {info.resolutions.length > 0 && (
                              <div className="sm:col-span-2">
                                <label className={labelCls}>What would you like?</label>
                                <select className={select} value={s.resolutionCode} onChange={(e) => setLine(line.orderItemId, { resolutionCode: e.target.value })}>
                                  <option value="">No preference</option>
                                  {info.resolutions.map((r) => (
                                    <option key={r.code} value={r.code}>
                                      {r.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                            <div className="sm:col-span-4">
                              <label className={labelCls}>Tell us more (optional)</label>
                              <input className={select} value={s.reasonText} maxLength={500} onChange={(e) => setLine(line.orderItemId, { reasonText: e.target.value })} placeholder="e.g. the box arrived crushed" />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {hasTrackings && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4">
              <h2 className="text-sm font-bold">How to reach you</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Email for updates</label>
                  <input type="email" className={input} value={email} onChange={(e) => setEmail(e.target.value)} placeholder={o.customerEmailMasked ? `Leave blank to use ${o.customerEmailMasked}` : 'you@example.com'} autoComplete="email" />
                </div>
                <div>
                  <label className={labelCls}>Phone (optional)</label>
                  <input className={input} value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" />
                </div>
              </div>
              <div>
                <label className={labelCls}>Anything else we should know? (optional)</label>
                <textarea className={input} rows={3} maxLength={2000} value={comment} onChange={(e) => setComment(e.target.value)} />
              </div>
              <Captcha key={captcha2Key} ref={captcha2Ref} onVerify={setCaptcha2} />
              {submitError && <Alert tone="error">{submitError}</Alert>}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-xs text-slate-500">
                  {selected.length === 0 ? 'Tick the items you want to return.' : `${selected.length} item${selected.length === 1 ? '' : 's'} selected.`}
                </p>
                <button type="submit" className={primary} disabled={submitting || blocked || selected.length === 0}>
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                  {submitting ? 'Submitting…' : 'Submit return request'}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </>,
  );
}
